import { RestrictionReason, type ProductRestriction } from "@models/product";
import { NO_LOCATION_PRODUCT_MESSAGE } from "@customer/_helpers/noLocationCopy";

// The one sentence a shopper is given for a product that cannot be offered to them, keyed on
// the block's `reason` (#410) — never on whether a destination field happens to be present.
//
// A destination refusal is built from the destination the SERVER resolved, not from the location
// the header happens to be labelling: those two can disagree (a stored id the API ruled
// ineligible falls back to the caller's first eligible location), and the refusal must name the
// destination the answer was actually computed for.
//
// `legalClass`, `legalClassName` and `restrictionId` never appear here — "Legal Class C" is
// internal taxonomy and means nothing to a shopper (Lead ruling 7).
export const restrictionMessage = (restriction: ProductRestriction): string => {
    // No destination at all: the caller is seated on no location (#417), so there is nowhere to
    // name and no location to switch to. Answered BEFORE the destination fields are read: the
    // API sends them null here, and the sentence must not depend on it.
    if (restriction.reason === RestrictionReason.NoDestination) {
        return NO_LOCATION_PRODUCT_MESSAGE;
    }

    const state = restriction.state?.trim();
    const zipCode = restriction.zipCode?.trim();

    if (state && zipCode) {
        return `This product can't be shipped to ${state} ${zipCode}.`;
    }

    if (state) {
        return `This product can't be shipped to ${state}.`;
    }

    // A destination restriction with no destination named: a legal class this build cannot name
    // fails closed for every caller (#411), destination or not. Still a refusal — just one that
    // cannot say where.
    return "This product can't be shipped to your selected location.";
};
